/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditEstimateDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-TIMELINE
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditEstimateDialog - Frontend UI module: EditEstimateDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парный END_METHOD, блок сохранения/лог.
 * END_CHANGE_SUMMARY
 */

import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import Alert from '@mui/material/Alert';
import { isEstimateInputValid } from '../../features/components/FeatureEstimateFields';

/* START_METHOD_EditEstimateDialog */
/* START_CONTRACT: EditEstimateDialog
 * PURPOSE: Диалог редактирования оценки роли внутри фичи прямо с таймлайна.
 * INPUTS: { open: boolean, onClose: ()=>void, featureName: string, roleName: string, currentEstimate: number|null, onSave: (n)=>Promise }.
 * OUTPUTS: { JSX.Element - диалог }.
 * SIDE_EFFECTS: вызов onSave (HTTP наверх); локальный state формы.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: EditEstimateDialog
 */
export default function EditEstimateDialog({
  open,
  onClose,
  featureName,
  roleName,
  currentEstimate,
  onSave,
}) {
  const firstFieldRef = useRef(null);
  const { dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setValue(currentEstimate == null ? '' : String(currentEstimate));
    setError('');
  }, [open, currentEstimate]);

  const trimmed = value.trim();
  const isValid = trimmed !== '' && isEstimateInputValid(trimmed);
  const canSave = isValid && !loading;

  /* START_METHOD_handleSave */
  /* START_CONTRACT: handleSave
   * PURPOSE: Сохранить новую оценку; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onSave (HTTP); setLoading/setError; onClose.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleSave
   */
  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError('');
    try {
      await onSave(parseFloat(trimmed));
      /* START_BLOCK_SAVE_OK */
      onClose();
      /* END_BLOCK_SAVE_OK */
    } catch (err) {
      /* START_BLOCK_SAVE_FAIL */
      console.warn('[EditEstimateDialog][handleSave][SAVE_FAIL] save estimate failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Не удалось сохранить оценку');
      /* END_BLOCK_SAVE_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSave */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ transition: dialogTransitionSlotProps }}>
      <DialogTitle sx={{ fontSize: '1rem' }}>Редактирование оценки</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {featureName}
          </Typography>
          {roleName && (
            <Typography variant="body2" color="text.secondary">
              {roleName}
            </Typography>
          )}
        </Box>
        <TextField
          inputRef={firstFieldRef}
          fullWidth
          label="Оценка (часы)"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          disabled={loading}
          error={trimmed !== '' && !isValid}
          helperText={trimmed !== '' && !isValid ? 'Значение от 0 до 10 000' : undefined}
          inputProps={{ min: 0, max: 10000, step: 'any' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleSave}
          disabled={!canSave}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
/* END_METHOD_EditEstimateDialog */
